<template>
  <div class="rt-editor">
    <div class="rt-toolbar">
      <el-tooltip content="粗体" placement="top">
        <button type="button" class="rt-btn" :class="{ active: activeStates.bold }" @mousedown.prevent @click="exec('bold')">
          <span class="rt-glyph bold">B</span>
        </button>
      </el-tooltip>
      <el-tooltip content="斜体" placement="top">
        <button type="button" class="rt-btn" :class="{ active: activeStates.italic }" @mousedown.prevent @click="exec('italic')">
          <span class="rt-glyph italic">I</span>
        </button>
      </el-tooltip>
      <el-tooltip content="下划线" placement="top">
        <button type="button" class="rt-btn" :class="{ active: activeStates.underline }" @mousedown.prevent @click="exec('underline')">
          <span class="rt-glyph underline">U</span>
        </button>
      </el-tooltip>
      <el-tooltip content="删除线" placement="top">
        <button type="button" class="rt-btn" :class="{ active: activeStates.strike }" @mousedown.prevent @click="exec('strikeThrough')">
          <span class="rt-glyph strike">S</span>
        </button>
      </el-tooltip>

      <el-divider direction="vertical" />

      <el-tooltip content="链接" placement="top">
        <button type="button" class="rt-btn" :class="{ active: activeStates.link }" @mousedown.prevent @click="setLink">
          <i class="el-icon-link"></i>
        </button>
      </el-tooltip>

      <el-divider direction="vertical" />

      <el-tooltip content="有序列表" placement="top">
        <button type="button" class="rt-btn" :class="{ active: activeStates.orderedList }" @mousedown.prevent @click="exec('insertOrderedList')">
          <i class="el-icon-s-order"></i>
        </button>
      </el-tooltip>
      <el-tooltip content="无序列表" placement="top">
        <button type="button" class="rt-btn" :class="{ active: activeStates.bulletList }" @mousedown.prevent @click="exec('insertUnorderedList')">
          <i class="el-icon-menu"></i>
        </button>
      </el-tooltip>

      <template v-if="!compact">
        <el-divider direction="vertical" />

        <el-tooltip content="插入表格" placement="top">
          <button type="button" class="rt-btn" @mousedown.prevent @click="insertTable">
            <i class="el-icon-grid"></i>
          </button>
        </el-tooltip>

        <el-divider direction="vertical" />

        <el-tooltip content="标题 1" placement="top">
          <button type="button" class="rt-btn" :class="{ active: activeStates.h1 }" @mousedown.prevent @click="toggleHeading('h1')">
            <span class="rt-glyph heading">H1</span>
          </button>
        </el-tooltip>
        <el-tooltip content="标题 2" placement="top">
          <button type="button" class="rt-btn" :class="{ active: activeStates.h2 }" @mousedown.prevent @click="toggleHeading('h2')">
            <span class="rt-glyph heading">H2</span>
          </button>
        </el-tooltip>
        <el-tooltip content="标题 3" placement="top">
          <button type="button" class="rt-btn" :class="{ active: activeStates.h3 }" @mousedown.prevent @click="toggleHeading('h3')">
            <span class="rt-glyph heading">H3</span>
          </button>
        </el-tooltip>
      </template>

      <el-divider direction="vertical" />

      <el-tooltip content="字体大小" placement="top">
        <el-select
          v-model="fontSizeVal"
          size="small"
          class="rt-size-select"
          placeholder="大小"
          clearable
          @change="onFontSizeChange"
        >
          <el-option v-for="s in FONT_SIZES" :key="s" :label="s" :value="s" />
        </el-select>
      </el-tooltip>

      <el-tooltip content="字体颜色" placement="top">
        <el-color-picker v-model="colorVal" size="small" @change="onColorChange" />
      </el-tooltip>

      <el-divider direction="vertical" />

      <el-popover v-model="emojiOpen" trigger="click" placement="bottom-start" width="328" popper-class="rt-emoji-popper">
        <button slot="reference" type="button" title="表情" class="rt-btn" :class="{ active: emojiOpen }" @mousedown.prevent>
          <span class="rt-glyph">😊</span>
        </button>
        <div class="rt-emoji-grid">
          <button v-for="e in EMOJIS" :key="e" type="button" class="rt-emoji-item" @click="insertEmoji(e)">{{ e }}</button>
        </div>
      </el-popover>

      <template v-if="quickReplies && quickReplies.length > 0">
        <el-divider direction="vertical" />
        <el-popover v-model="qrOpen" trigger="click" placement="bottom-end" width="280" popper-class="rt-qr-popper">
        <button slot="reference" type="button" title="常用語" class="rt-btn" :class="{ active: qrOpen }" @mousedown.prevent>
          <i class="el-icon-document-copy"></i>
        </button>
        <div class="rt-qr-list">
            <div v-for="group in quickReplies" :key="group.id" class="rt-qr-group">
              <div class="rt-qr-group-name">{{ group.name }}</div>
              <div v-for="reply in group.replies || []" :key="reply.id" class="rt-qr-item" @click="selectQuickReply(reply.content)">
                {{ reply.title }}
              </div>
            </div>
          </div>
        </el-popover>
      </template>
    </div>

    <div
      ref="root"
      class="rt-content"
      contenteditable="true"
      :data-placeholder="placeholder || ''"
      :style="{ minHeight: (compact ? 40 : localMinHeight) + 'px' }"
      @input="onInput"
      @keydown="onKeyDown"
      @click="onClick"
      @paste="onPaste"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'RichTextEditor',
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    minHeight: { type: Number, default: 120 },
    compact: { type: Boolean, default: false },
    quickReplies: { type: Array, default: () => [] }
  },
  data() {
    return {
      localMinHeight: this.minHeight,
      emojiOpen: false,
      qrOpen: false,
      fontSizeVal: undefined,
      colorVal: undefined,
      activeStates: {
        bold: false,
        italic: false,
        underline: false,
        strike: false,
        link: false,
        orderedList: false,
        bulletList: false,
        h1: false,
        h2: false,
        h3: false
      },
      FONT_SIZES: ['12px', '14px', '16px', '18px', '20px', '24px'],
      EMOJIS: [
        '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
        '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😋', '😜', '🤪',
        '🤗', '🤭', '🤔', '😏', '😒', '🙄', '😬', '😮', '😱', '😳',
        '🥺', '😢', '😭', '😴', '😌', '😟', '😕', '😤', '😡',
        '👋', '✌️', '👌', '🤞', '🤝', '🙏', '👍', '👎', '👏', '💪',
        '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🔥', '⭐', '✨',
        '🎉', '🎊', '🎁', '💰', '📈', '📉', '⏰', '✅', '❌', '⚠️'
      ]
    }
  },
  watch: {
    value(val) {
      const root = this.$refs.root
      if (root && (val || '') !== root.innerHTML) {
        root.innerHTML = val || ''
        this.updateEmptyState()
      }
    }
  },
  mounted() {
    const root = this.$refs.root
    if (root) {
      root.innerHTML = this.value || ''
      this.updateEmptyState()
    }
    document.addEventListener('selectionchange', this.onDocSelectionChange)
  },
  beforeDestroy() {
    document.removeEventListener('selectionchange', this.onDocSelectionChange)
  },
  methods: {
    tryCmdState(cmd) {
      try {
        return document.queryCommandState(cmd)
      } catch (e) {
        return false
      }
    },
    tryCmdValue(cmd) {
      try {
        return String(document.queryCommandValue(cmd) || '')
      } catch (e) {
        return ''
      }
    },
    selectionInsideEditor() {
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return false
      const node = sel.getRangeAt(0).commonAncestorContainer
      const el = node.nodeType === window.Node.ELEMENT_NODE ? node : node.parentElement
      const root = this.$refs.root
      return !!el && !!root && root.contains(el)
    },
    updateActiveStates() {
      if (!this.selectionInsideEditor()) return
      this.activeStates.bold = this.tryCmdState('bold')
      this.activeStates.italic = this.tryCmdState('italic')
      this.activeStates.underline = this.tryCmdState('underline')
      this.activeStates.strike = this.tryCmdState('strikeThrough')
      this.activeStates.link = this.tryCmdState('link') || this.isCaretInLink()
      this.activeStates.orderedList = this.tryCmdState('insertOrderedList')
      this.activeStates.bulletList = this.tryCmdState('insertUnorderedList')
      const block = this.tryCmdValue('formatBlock').toLowerCase()
      this.activeStates.h1 = block === 'h1'
      this.activeStates.h2 = block === 'h2'
      this.activeStates.h3 = block === 'h3'
    },
    isCaretInLink() {
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return false
      const node = sel.getRangeAt(0).commonAncestorContainer
      const el = node.nodeType === window.Node.ELEMENT_NODE ? node : node.parentElement
      return !!el && !!el.closest && !!el.closest('a')
    },
    onDocSelectionChange() {
      this.updateActiveStates()
    },
    setStyleWithCSS(on) {
      try {
        document.execCommand('styleWithCSS', false, String(on))
      } catch (e) { /* ignore */ }
    },
    syncModel() {
      const root = this.$refs.root
      if (!root) return
      this.updateEmptyState()
      this.$emit('input', root.innerHTML)
    },
    updateEmptyState() {
      const root = this.$refs.root
      if (!root) return
      const hasMedia = !!root.querySelector('img, table, li, hr, iframe')
      const empty = !root.textContent || !root.textContent.trim()
      root.classList.toggle('is-empty', empty && !hasMedia)
    },
    exec(cmd, value) {
      const root = this.$refs.root
      if (!root) return
      root.focus()
      document.execCommand(cmd, false, value)
      this.syncModel()
      this.updateActiveStates()
    },
    normalizeFontTags() {
      const root = this.$refs.root
      if (!root) return
      const fonts = root.querySelectorAll('font[size]')
      Array.prototype.slice.call(fonts).forEach(f => {
        const idx = parseInt(f.getAttribute('size') || '0', 10)
        const px = this.FONT_SIZES[idx - 1]
        const span = document.createElement('span')
        if (px) span.style.fontSize = px
        span.innerHTML = f.innerHTML
        f.parentNode && f.parentNode.replaceChild(span, f)
      })
    },
    onFontSizeChange(v) {
      if (!v) return
      const root = this.$refs.root
      if (!root) return
      root.focus()
      this.setStyleWithCSS(false)
      const idx = this.FONT_SIZES.indexOf(v)
      document.execCommand('fontSize', false, String(idx + 1))
      this.normalizeFontTags()
      this.syncModel()
    },
    onColorChange(v) {
      if (!v) return
      const root = this.$refs.root
      if (!root) return
      root.focus()
      this.setStyleWithCSS(true)
      document.execCommand('foreColor', false, v)
      this.syncModel()
    },
    setLink() {
      const root = this.$refs.root
      if (!root) return
      let prev = ''
      if (this.isCaretInLink()) prev = this.tryCmdValue('link')
      const url = window.prompt('输入链接 URL', prev)
      if (url === null) return
      root.focus()
      if (url === '') {
        document.execCommand('unlink')
      } else {
        document.execCommand('createLink', false, url)
        Array.prototype.slice.call(root.querySelectorAll('a')).forEach(a => {
          a.setAttribute('target', '_blank')
          a.setAttribute('rel', 'noopener noreferrer')
        })
      }
      this.syncModel()
      this.updateActiveStates()
    },
    toggleHeading(level) {
      const current = this.tryCmdValue('formatBlock').toLowerCase()
      this.exec('formatBlock', current === level ? '<p>' : '<' + level + '>')
    },
    insertTable() {
      const root = this.$refs.root
      if (!root) return
      root.focus()
      const html =
        '<table><thead><tr><th>表头 1</th><th>表头 2</th><th>表头 3</th></tr></thead>' +
        '<tbody><tr><td><br></td><td><br></td><td><br></td></tr><tr><td><br></td><td><br></td><td><br></td></tr></tbody></table>'
      document.execCommand('insertHTML', false, html)
      this.syncModel()
    },
    insertEmoji(emoji) {
      this.exec('insertText', emoji)
      this.emojiOpen = false
    },
    selectQuickReply(content) {
      this.setContent(content)
      this.focusEnd()
      this.qrOpen = false
    },
    onInput() {
      this.syncModel()
    },
    onKeyDown(e) {
      if (this.compact && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.$emit('submit')
      }
    },
    onClick(e) {
      this.updateActiveStates()
      const target = e.target
      if (target && target.closest && target.closest('a')) {
        e.preventDefault()
      }
    },
    onPaste(e) {
      e.preventDefault()
      const text = (e.clipboardData && e.clipboardData.getData('text/plain')) || ''
      if (text) document.execCommand('insertText', false, text)
      this.syncModel()
    },
    // 对外 API（父级通过 $refs 调用）
    setContent(html) {
      const root = this.$refs.root
      if (!root) return
      root.innerHTML = html || ''
      this.syncModel()
    },
    clear() {
      this.setContent('')
    },
    focusEnd() {
      const root = this.$refs.root
      if (!root) return
      root.focus()
      const range = document.createRange()
      range.selectNodeContents(root)
      range.collapse(false)
      const sel = window.getSelection()
      if (sel) {
        sel.removeAllRanges()
        sel.addRange(range)
      }
    },
    getHTML() {
      const root = this.$refs.root
      return root ? root.innerHTML : ''
    }
  }
}
</script>

<style scoped>
.rt-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
}
.rt-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border-bottom: 1px solid #ebeef5;
  background: #f5f7fa;
}
.rt-btn {
  min-width: 28px;
  height: 24px;
  padding: 0 4px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #303133;
  font-size: 13px;
}
.rt-btn:hover {
  background: #f0f2f5;
}
.rt-btn.active {
  background: #4f46e5;
  color: #fff;
}
.rt-glyph {
  font-size: 13px;
  line-height: 1;
}
.rt-glyph.bold { font-weight: 700; }
.rt-glyph.italic { font-style: italic; }
.rt-glyph.underline { text-decoration: underline; }
.rt-glyph.strike { text-decoration: line-through; }
.rt-glyph.heading { font-size: 11px; font-weight: 700; }
.rt-size-select { width: 78px; }
.rt-content {
  padding: 8px 12px;
  outline: none;
  overflow-y: auto;
  max-height: 360px;
}
.rt-content.is-empty:before {
  content: attr(data-placeholder);
  color: #c0c4cc;
}
.rt-emoji-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;
  max-height: 280px;
  overflow-y: auto;
}
.rt-emoji-item {
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  border-radius: 4px;
  padding: 3px 0;
}
.rt-emoji-item:hover { background: #f0f2f5; }
.rt-qr-list { max-height: 340px; overflow-y: auto; }
.rt-qr-group { margin-bottom: 2px; }
.rt-qr-group-name {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  padding: 6px 10px 2px;
  border-bottom: 1px solid #ebeef5;
}
.rt-qr-item {
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 13px;
}
.rt-qr-item:hover { background: #f0f2f5; }
</style>